/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/user_profiles-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-user_profiles-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/user_profiles-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, organization_id, user_id, active_subscription_id, role_id, stripe_customer_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, email, full_name
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/user_profiles-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-user_profiles-api-client.ts (API client)
 * - client/api/generated_schemas/user_profiles.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-06-22T23:44:13.693Z
 * Source config: config/entities/user_profiles-entity-config.ts
 * Public fields: 34/34 total fields
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfilesClient,
  UserProfilesQueryOptions,
} from "../generated_clients/generated-user_profiles-api-client.js";
import {
  UserProfilesInsert,
  UserProfilesUpdate,
  UserProfilesFieldConfig,
} from "../../types/generated_schemas/generated-user_profiles-schema.js";

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/user_profiles_business_hooks/

// Query keys with enhanced caching strategy
export const userProfilesKeys = {
  all: ["user_profiles"] as const,
  lists: () => [...userProfilesKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...userProfilesKeys.lists(), { filters }] as const,
  details: () => [...userProfilesKeys.all, "detail"] as const,
  detail: (id: string) => [...userProfilesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) =>
    [...userProfilesKeys.all, "search", query, fields] as const,
  filter: (filters: Record<string, any>) =>
    [...userProfilesKeys.all, "filter", filters] as const,
  sort: (sortBy: string, ascending: boolean) =>
    [...userProfilesKeys.all, "sort", sortBy, ascending] as const,
  count: (filters?: Record<string, any>) =>
    [...userProfilesKeys.all, "count", filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useUserProfilesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new UserProfilesClient();
};

/**
 * Hook to get all user_profiles with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with user_profiles data
 */
export const useUserProfilesList = (options?: UserProfilesQueryOptions) => {
  const client = useUserProfilesClient();

  return useQuery({
    queryKey: userProfilesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single user_profile by ID
 *
 * @param id - The user_profile ID
 * @returns React Query result with single user_profile data
 */
export const useUserProfiles = (id: string) => {
  const client = useUserProfilesClient();

  return useQuery({
    queryKey: userProfilesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/user_profiles
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/user_profiles_business_hooks/
 */

/**
 * Hook to create a new user_profile
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, email, full_name
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating user_profile
 */
export const useCreateUserProfiles = () => {
  const client = useUserProfilesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserProfilesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProfilesKeys.all });
    },
  });
};

/**
 * Hook to update an existing user_profile
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating user_profile
 */
export const useUpdateUserProfiles = () => {
  const client = useUserProfilesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserProfilesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userProfilesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userProfilesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a user_profile
 *
 * @returns React Query mutation for deleting user_profile
 */
export const useDeleteUserProfiles = () => {
  const client = useUserProfilesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProfilesKeys.all });
    },
  });
};

/**
 * Hook to count user_profiles with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, organization_id, user_id, active_subscription_id, role_id, stripe_customer_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useUserProfilesCount = (
  filter?: Partial<
    Record<(typeof UserProfilesFieldConfig.filterable)[number], any>
  >,
) => {
  const client = useUserProfilesClient();

  return useQuery({
    queryKey: userProfilesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useUserProfilesFieldConfig = () => {
  return UserProfilesFieldConfig;
};
